import { Colors } from '@oy/uicore';
import {
  Backdrop,
  IconButton,
  Loader,
  makeId,
  Spacer,
  Text,
} from '@oy/uidesktop';
import { SystemIcon } from '@oy/uiicon';
import classNames from 'classnames';
import React, { lazy, Suspense } from 'react';
import { RouteProps, useNavigate } from 'react-router-dom';
import { MainLoader, Sidebar } from 'src/components';
import { FloatingActionButton } from 'src/components/FloatingActionButton/FloatingActionButton';
import UpgradeHeader from 'src/components/UpgradeHeader/UpgradeHeader';
import { IS_WHITELABELING } from 'src/constants';
import { UpgradeAlertData } from 'src/constants/UpgradeAlert';
import { useTranslate } from 'src/hooks';
import { HelpAndDocumentationContext } from 'src/pages/HelpAndDocumentation/hooks/useHelpAndDocumentationContext';
import styles from './MainLayout.module.css';
import { useMainLayoutImpl } from './useMainLayoutImpl';

const LoginModal = lazy(
  () => import('src/pages/Auth/components/LoginModal/LoginModal'),
);
const OtpModal = lazy(
  () => import('src/pages/Auth/components/OtpModal/OtpModal'),
);

// const AutoWithdrawalConfigReminderBanner = lazy(
//   () =>
//     import(
//       'src/components/AutoWithdrawalConfigReminderBanner/AutoWithdrawalConfigReminderBanner'
//     ),
// );
const ArticleSideModal = lazy(
  () =>
    import(
      'src/pages/HelpAndDocumentation/components/ArticleSideModal/ArticleSideModal'
    ),
);
const CategorySideModal = lazy(
  () =>
    import(
      'src/pages/HelpAndDocumentation/components/CategorySideModal/CategorySideModal'
    ),
);
const UserFeedbackModal = lazy(() =>
  import('src/components/UserFeedbackModal/UserFeedbackModal').then(
    component => ({ default: component.UserFeedbackModal }),
  ),
);

const UpdateDataBanner = lazy(
  () => import('src/components/UpdateDataBanner/UpdateDataBanner'),
);
const ChargebackBanner = lazy(
  () => import('src/components/ChargebackBanner/ChargebackBanner'),
);
const VCCMaintenanceBanner = lazy(
  () =>
    import(
      'src/pages/VirtualCreditCard/components/Banner/VCCMaintenanceBanner'
    ),
);
const HowToTopupModal = lazy(() =>
  import('src/components/HowToTopupModal/HowToTopupModal').then(component => ({
    default: component.HowToTopupModal,
  })),
);

const RightSidebar = lazy(() =>
  import('src/components/RightSidebar').then(component => ({
    default: component.RightSidebar,
  })),
);
const SomethingNewButton = lazy(() =>
  import('src/components/RightSidebar/SomethingNewButton').then(component => ({
    default: component.SomethingNewButton,
  })),
);
const PasswordChangeModal = lazy(() =>
  import('src/components').then(component => ({
    default: component.PasswordChangeModal,
  })),
);
const Clarity = lazy(() =>
  import('src/components').then(component => ({
    default: component.Clarity,
  })),
);
const MobileExperienceWarning = lazy(() =>
  import('src/components').then(component => ({
    default: component.MobileExperienceWarning,
  })),
);

type MainLayoutProps = {
  actionButton: React.ReactNode;
  backButton?: boolean;
  backButtonUrl?: string;
  children?: any;
  pageTitle?: string;
  showBreadcrumb?: boolean;
  showHeader?: boolean;
  showMessenger?: boolean;
  showSideBar?: boolean;
  upgradeAlert?: UpgradeAlertData;
  helpCategoryIndex?: number;
  subCategoryPrefix?: string[];
} & RouteProps;

const MainLayout = ({
  actionButton,
  backButton,
  children,
  pageTitle,
  upgradeAlert,
  backButtonUrl,
  showHeader = true,
  showMessenger = true,
  showSideBar = true,
  helpCategoryIndex,
  subCategoryPrefix,
}: MainLayoutProps) => {
  const { getTranslation } = useTranslate();
  const shouldShowChargebackBanner = IS_WHITELABELING
    ? false
    : new Date() >= new Date('2025-01-21 10:00:00');

  const navigate = useNavigate();
  const { state, toggleRightbar, onClickHelp } = useMainLayoutImpl(
    pageTitle ?? '',
  );
  const {
    isGeneralLoading,
    isProdEnv,
    loadingPaymentInfo,
    whatsNewPreview,
    showRightbar,
    showWhatsNewButton,
    helpAndDocumentationContext,
    neverClickedHelpButton,
  } = state;

  return (
    <Suspense fallback={<MainLoader />}>
      <HelpAndDocumentationContext.Provider value={helpAndDocumentationContext}>
        <div className={classNames(styles.pageWrapper, styles.newPageWrapper)}>
          {showSideBar && <Sidebar />}
          {isGeneralLoading && (
            <Backdrop show>
              <div className={styles.loaderContainer}>
                <Loader title={getTranslation('common_pleaseWait')} />
              </div>
            </Backdrop>
          )}

          <HowToTopupModal />

          <main className={showHeader ? styles.main : styles.mainFullWidth}>
            {!isProdEnv && (
              <div className={classNames(styles.banner, styles.staging)}>
                <Text textStyle={Text.Style.BodyMdRegular} color={Colors.WHITE}>
                  {getTranslation('mainLayout_banner_demoMode')}
                </Text>
              </div>
            )}

            {/* {showHeader && isProdEnv && <AutoWithdrawalConfigReminderBanner />} */}
            {showHeader && isProdEnv && <UpdateDataBanner />}
            {pageTitle === getTranslation('corporateCard_PageTitle') && (
              <VCCMaintenanceBanner />
            )}
            {showHeader && isProdEnv && shouldShowChargebackBanner && (
              <ChargebackBanner />
            )}
            {upgradeAlert && <UpgradeHeader upgradeAlertData={upgradeAlert} />}
            <div
              id="content"
              className={showSideBar ? styles.content : styles.contentFullWidth}
            >
              {loadingPaymentInfo ? (
                <Loader title={getTranslation('common_pleaseWait')} />
              ) : (
                <>
                  <div
                    className={classNames(styles.pageTitle, {
                      [styles.pageTitleHidden]: !showHeader,
                    })}
                  >
                    <Text textStyle={Text.Style.HeadingMd} id="page">
                      {backButton && (
                        <span
                          id={makeId('ttl', pageTitle)}
                          className={styles.backButton}
                          onClick={() => {
                            return backButtonUrl
                              ? navigate(backButtonUrl)
                              : navigate(-1);
                          }}
                          role="button"
                        >
                          <SystemIcon name="back" color={Colors.BLACK100} />
                        </span>
                      )}
                      {pageTitle}
                    </Text>
                    {showHeader && (
                      <div className={styles.pageTitleRightContent}>
                        {actionButton && (
                          <Spacer className={styles.actionButton}>
                            {actionButton}
                          </Spacer>
                        )}
                        {showWhatsNewButton && (
                          <SomethingNewButton onClick={toggleRightbar} />
                        )}
                        {typeof helpCategoryIndex === 'number' && (
                          <IconButton
                            iconName="faq"
                            color={IconButton.Color.Secondary}
                            buttonType={IconButton.Type.SquareOutlined}
                            className={styles.helpButton}
                            data-never-clicked={neverClickedHelpButton}
                            onClick={() =>
                              onClickHelp(helpCategoryIndex, subCategoryPrefix)
                            }
                          />
                        )}
                      </div>
                    )}
                  </div>
                  <Suspense
                    fallback={
                      <Loader title={getTranslation('common_pleaseWait')} />
                    }
                  >
                    {children}
                  </Suspense>
                </>
              )}
            </div>
          </main>
          <FloatingActionButton showButton={showMessenger} />
          <Clarity />
          <LoginModal />
          <OtpModal />
          <MobileExperienceWarning />
          <PasswordChangeModal />
          <UserFeedbackModal />
          {showRightbar && showWhatsNewButton && (
            <RightSidebar
              toggleRightbar={toggleRightbar}
              announcements={whatsNewPreview || []}
            />
          )}
        </div>
        <ArticleSideModal />
        <CategorySideModal />
      </HelpAndDocumentationContext.Provider>
    </Suspense>
  );
};

export default MainLayout;
